import { Locale } from '@core/app/types/globalApiType';
import { AddCommentRequest, CalendarVariablesRequest, CalendarVariablesResponse, DeleteCommentRequest, GetCommentsResponse, JobsRequest, JobsResponse, JobsResponseData, LoggedHoursResponse, LoggedWorkByIdData, LoggedWorkByIdResponse, SetAdditionRequest, SetHoursRequest, SetHoursResponse, SetManualHoursRequest, SetPieceworkRequest, SkipShiftRequest, UpdateLoggedWorkAbsenceRequest, UpdateLoggedWorkAdditionRequest, UpdateLoggedWorkHourRequest, UpdateLoggedWorkPieceworkRequest } from '@core/app/types/logworkApiType';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const logworkApi = createApi({
    reducerPath: 'logworkApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['LoggedWorkById', 'MainData'],
    endpoints: (builder) => ({
        getLocale: builder.query<Locale, void>({
            query: () => ({
                url: '/locale',
                method: 'GET',
            }),
        }),
        getData: builder.query({
            query: (arg) => {
                const { fromDate, toDate, scopes } = arg
                return {
                    url: '/employee/calendar/v2',
                    method: 'GET',
                    params: {
                        fromDate,
                        toDate,
                        scopes
                    }
                }
            },
            providesTags: ['MainData'],
            transformResponse: (response: any) => {
                return response.data
            },
        }),
        getJobs: builder.mutation<JobsResponseData, JobsRequest>({
            query: (body) => ({
                url: '/employee/work/jobs',
                method: 'POST',
                body
            }),
            transformResponse: (response: JobsResponse) => {
                return response.data
            },
        }),
        setLoggedHours: builder.mutation<any, SetHoursRequest | SetManualHoursRequest | SetPieceworkRequest | SetAdditionRequest>({
            query: (body) => {
                let url = '/employee/work/log'

                if ('addition' in body.data) {
                    url = '/employee/work/log/addition'
                }
        
                return {
                    url: url,
                    method: 'POST',
                    body: body.data,
                }
            },
            transformResponse: (response: SetHoursResponse) => response.data.result,
            invalidatesTags: ['MainData'],
        }),
        getLoggedHours: builder.mutation<any, JobsRequest>({
            query: (body) => ({
                url: '/employee/work/logged-work',
                method: 'POST',
                body
            }),
            transformResponse: (response: LoggedHoursResponse) => response.data,
        }),
        deleteLoggedHours: builder.mutation<any, { ids: number[], date: {
            start: string
            end: string
        } }>({
            query: (body) => ({
                url: '/employee/work/delete',
                method: 'DELETE',
                body
            }),
            invalidatesTags: ['MainData'],
        }),
        getCalendarVariables: builder.mutation<any, CalendarVariablesRequest>({
            query: (body) => ({
                url: '/employee/calendar',
                method: 'POST',
                body
            }),
            transformResponse: (response: CalendarVariablesResponse) => response.data
        }),
        skipShift: builder.mutation<void, SkipShiftRequest>({
            query: (body) => ({
                url: '/employee/calendar/skip/shift',
                method: 'POST',
                body
            }),
            invalidatesTags: ['MainData'],
        }),
        getLoggedWorkById: builder.query<LoggedWorkByIdData, number>({
            query: (id) => ({
                url: `/employee/work/${id}`,
                method: 'GET',
            }),
            transformResponse: (response: LoggedWorkByIdResponse) => response.data,
            providesTags: ['LoggedWorkById'],
        }),
        deleteFileFromWork: builder.mutation<void, { fileId: number, workHourId: number, dates: { start: string, end: string } }>({
            query: (body) => ({
                url: `/employee/work/${body.workHourId}/delete/${body.fileId}`,
                method: 'DELETE',
                body
            }),
            invalidatesTags: ['LoggedWorkById', 'MainData'],
        }),
        updateLoggedWorkHour: builder.mutation<void, UpdateLoggedWorkHourRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/hour`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['LoggedWorkById', 'MainData']
        }),
        updateLoggedWorkPiecework: builder.mutation<void, UpdateLoggedWorkPieceworkRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/piecework`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['LoggedWorkById', 'MainData'],
        }),
        updateLoggedWorkAddition: builder.mutation<void, UpdateLoggedWorkAdditionRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/addition`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['LoggedWorkById', 'MainData'],
        }),
        updateAbsenceInLogwork: builder.mutation<void, UpdateLoggedWorkAbsenceRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/absence`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['LoggedWorkById', 'MainData'],
        }),
        getCommentsByWorkHourId: builder.query<GetCommentsResponse, number>({
            query: (workHourId) => ({
                url: `/employee/work/${workHourId}/comments`,
                method: 'GET',
            }),
        }),
        addComment: builder.mutation<Comment, AddCommentRequest>({
            query: (body) => ({
                url: `/employee/work/${body.workHourId}/comment`,
                method: 'POST',
                body: { workHourId: body.workHourId, comment: body.comment },
            }),
            transformResponse: (response: { statusCode: number; success: boolean; message: string; data: Comment }) => response.data,
            invalidatesTags: ['LoggedWorkById'],
        }),
        deleteComment: builder.mutation<{ success: boolean }, DeleteCommentRequest>({
            query: (body) => ({
                url: `/employee/work/${body.workHourId}/comment/${body.commentId}`,
                method: 'DELETE',
            }),
            transformResponse: (response: { statusCode: number; success: boolean; message: string; data: { success: boolean } }) => response.data,
        }),
        uploadFileToWork: builder.mutation({
            query: (body) => ({
                url: `/employee/work/${body.id}/upload`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['LoggedWorkById']
        }),
        sendSwitchFeedback: builder.mutation<void, { message: string, url: string }>({
            query: (body) => ({
                url: '/employee/switch-feedback',
                method: 'POST',
                body
            })
        })
    }),
})

export const {
    useGetLocaleQuery, 
    useGetJobsMutation, 
    useSetLoggedHoursMutation, 
    useGetLoggedHoursMutation, 
    useDeleteLoggedHoursMutation, 
    useGetCalendarVariablesMutation, 
    useSkipShiftMutation, 
    useGetLoggedWorkByIdQuery,
    useDeleteFileFromWorkMutation,
    useUpdateLoggedWorkHourMutation,
    useUpdateLoggedWorkPieceworkMutation, 
    useUpdateLoggedWorkAdditionMutation,
    useGetCommentsByWorkHourIdQuery,
    useAddCommentMutation,
    useDeleteCommentMutation,
    useUpdateAbsenceInLogworkMutation,
    useUploadFileToWorkMutation,
    useLazyGetDataQuery,
    useSendSwitchFeedbackMutation
} = logworkApi
